import { useState, type ReactElement } from 'react';
import styles from './registration-card-remake.module.scss';

import { ColorTypesEnum } from '@utmb/types/colors';
import { Font } from '@utmb/design-system/text/font';
import { getCategoryColor, getColorStyle } from '@utmb/utils/colors';
import { DashboardMemberWorldSerieRegistrationView } from '@utmb/types/index';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';

import { Image } from '@utmb/design-system/image/Image';
import { CloudinaryTransformEnum } from '@utmb/types/cloudinary-transform.enum';
import { Flag } from '@utmb/design-system/flag/Flag';
import { RegistrationStatusEnum } from '@utmb/types/enum/RegistrationStatusEnum';
import { ButtonRemake } from '@utmb/design-system/button-remake/ButtonRemake';
import router from 'next/router';
import { RegistrationServiceModal } from '@utmb/features/my-registrations/RegistrationServiceModal';
import { useTranslation } from 'next-i18next';
import { IconsEnum } from '@utmb/types/icons';
import Countdown from '@utmb/design-system/countdown-remake/Countdown';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { useUser } from '@utmb/contexts/AuthContext';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { RegistrationGroupModal } from '@utmb/features/my-registrations/registration-group-modal/RegistrationGroupModal';

export interface RegistrationCardRemakeProps {
    data: DashboardMemberWorldSerieRegistrationView;
}

export function RegistrationCardRemake({ data }: RegistrationCardRemakeProps): ReactElement {
    const openExtraFileNumber = router.query.openExtra;
    const { t } = useTranslation();
    const [isServiceModalOpen, setIsServiceModalOpen] = useState(openExtraFileNumber === data.fileNumber);
    const [isGroupDetailModalOpen, setIsGroupDetailModalOpen] = useState(false);
    const { getToken } = useUser();
    const { refreshMember } = useDashboard();

    async function handleCancelRace(raceYearId: number, tenant: string): Promise<void> {
        if (confirm(t('common:races.preregistration.confirmCancel'))) {
            const token = await getToken();
            fetchTheAPI({
                isClient: true,
                tenantId: tenant,
                bearer: token,
                method: FetchMethodsEnum.DELETE,
                path: `/registration/pre-register-race-year/${raceYearId}`,
            })
                .then(() => {
                    refreshMember();
                })
                .catch((error) => {
                    alert(error);
                });
        }
    }

    return (
        <div className={styles.registrationCard}>
            <div className={styles.wrapImage}>
                <div className={styles.wrapStatus}>
                    <Font.OxaniumBold
                        className={[
                            styles.status,
                            data.status === RegistrationStatusEnum.CANCELLED ||
                            data.status === RegistrationStatusEnum.PREREGISTERED_CANCELLED ||
                            data.status === RegistrationStatusEnum.PREREGISTERED_DRAW_OK_CANCELLED
                                ? styles.cancelled
                                : data.status === RegistrationStatusEnum.PREREGISTERED
                                ? styles.prereg
                                : data.status === RegistrationStatusEnum.PREREGISTERED_DRAW_KO
                                ? styles.preregko
                                : data.status === RegistrationStatusEnum.PREREGISTERED_DRAW_OK
                                ? styles.preregok
                                : data.status === RegistrationStatusEnum.TRANSFERRED
                                ? styles.transferred
                                : styles.reg,
                        ].join(' ')}
                        as="span"
                        desktop="16"
                        mobile="16"
                        color={getColorStyle(ColorTypesEnum.WHITE)}
                    >
                        {t(`myutmb:myUtmb.registration.status.${data.status}`)}
                    </Font.OxaniumBold>
                    {data.hasBoost && data.boostPct && data.boostPct > 0 ? (
                        <Font.OxaniumBold className={styles.boost} as="span" desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.PRIMARY)}>{`${t(
                            'races.preregistration.boost'
                        )} ${data.boostPct}%`}</Font.OxaniumBold>
                    ) : null}
                </div>
                <Image
                    className={styles.background}
                    objectFit="cover"
                    layout="fill"
                    image={pIdToCloudinary(data.background, data.eventName)}
                    addOptions={[CloudinaryTransformEnum.TRIM]}
                />

                <Image
                    className={styles.logo}
                    objectFit="contain"
                    layout="fixed"
                    width={90}
                    height={90}
                    image={pIdToCloudinary(data.logo, data.eventName)}
                    addOptions={[CloudinaryTransformEnum.TRIM]}
                />

                <Countdown targetDate={new Date(data.date)} />
            </div>
            <div className={styles.wrapContent} style={{ borderColor: getCategoryColor(data.category) }}>
                <div className={styles.content}>
                    <div className={styles.wrapHeader}>
                        <div className={styles.header}>
                            <div className={styles.wrapTitle}>
                                <Font.OxaniumBold className={styles.title} as="span" desktop="30" mobile="22" color={getCategoryColor(data.category)}>
                                    {data.raceName}
                                </Font.OxaniumBold>
                                <PiCategoryLogo piCategory={data.category} height={20} isDark={true} />
                            </div>

                            <div className={styles.wrapDateCountry}>
                                <Font.OxaniumBold className={styles.date} as="span" desktop="16" mobile="14" color={getColorStyle(ColorTypesEnum.PRIMARY)}>
                                    {data.date}
                                </Font.OxaniumBold>
                                <div className={styles.separator} />
                                <div className={styles.location}>
                                    <Flag isoCode={data.countryCode} hideShadow />
                                    <Font.OxaniumMedium
                                        className={styles.location}
                                        as="span"
                                        desktop="16"
                                        mobile="16"
                                        color={getColorStyle(ColorTypesEnum.PRIMARY)}
                                    >
                                        {data.countryName}
                                    </Font.OxaniumMedium>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.actions}>
                        <ButtonRemake
                            action={`https://${data.tenant}.utmb.world`}
                            target="_blank"
                            label={t('myutmb:myUtmb.registration.eventSite')}
                            variant="transparent"
                            icon={IconsEnum.HELP}
                            customClassName={styles.eventSiteButton}
                        />
                        {data.url && (data.status !== RegistrationStatusEnum.PREREGISTERED || data.canChange) && (
                            <>
                                {data.status === RegistrationStatusEnum.PREREGISTERED_DRAW_OK ? (
                                    <ButtonRemake
                                        action={data.url}
                                        label={t('myutmb:myUtmb.registration.finalizeButton')}
                                        variant="transparent"
                                        icon={IconsEnum.CHECKBOX}
                                        customClassName={styles.finalizeButton}
                                    />
                                ) : (
                                    <div className={styles.card_actions_registration}>
                                        <ButtonRemake
                                            action={data.url}
                                            label={t('myutmb:myUtmb.registration.editLabel')}
                                            variant="transparent"
                                            icon={IconsEnum.PEN}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {data.status === RegistrationStatusEnum.REGISTERED && data.hasOtherService && (
                            <div>
                                <ButtonRemake
                                    label={t('myutmb:myUtmb.registration.extraService')}
                                    action={() => setIsServiceModalOpen(!isServiceModalOpen)}
                                    variant="transparent"
                                    icon={IconsEnum.BUS}
                                />
                                <RegistrationServiceModal
                                    close={setIsServiceModalOpen}
                                    isOpen={isServiceModalOpen}
                                    raceYearId={data.raceYearId}
                                    tenant={data.tenant}
                                    year={data.year}
                                />
                            </div>
                        )}
                        {data.status === RegistrationStatusEnum.REGISTERED && data.urlResaTransports && (
                            <div>
                                <ButtonRemake
                                    action={data.urlResaTransports}
                                    target="_blank"
                                    label={t('myutmb:myUtmb.registration.bookSupporterPass')}
                                    variant="transparent"
                                    icon={IconsEnum.BUS}
                                />
                            </div>
                        )}

                        {data.grp && data.otherGroupMembers ? (
                            <div>
                                <ButtonRemake
                                    action={() => setIsGroupDetailModalOpen(true)}
                                    target="_blank"
                                    label={`${t('races.preregistration.group')} ${data.grp}`}
                                    variant="transparent"
                                    icon={IconsEnum.USER}
                                    customClassName={styles.grpButton}
                                    noTranslate
                                />
                                <RegistrationGroupModal data={data} isOpen={isGroupDetailModalOpen} close={() => setIsGroupDetailModalOpen(false)} />
                            </div>
                        ) : null}
                        {data.status === RegistrationStatusEnum.PREREGISTERED && data.canChange ? (
                            <ButtonRemake
                                action={() => handleCancelRace(data.raceYearId, data.tenant)}
                                target="_blank"
                                label={t('races.preregistration.msgToCancel')}
                                variant="transparent"
                                icon={IconsEnum.CLOSE}
                                customClassName={styles.eventSiteButton}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
